// Shell layout: top nav with logo + nav links + signed-in identity
// card, content slot, footer. Per SPEC §3.5 the chrome carries the
// "static identity card" (Account Holder name) — Subject switcher is
// post-MVP per §4.5 so we just show the email + role + tenant id.

import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { auth } from "../lib/api";
import { useAuthStore } from "../lib/store";
import { pushToast, ToastContainer } from "./Toast";
import { useEhrRedirectToast } from "../hooks/useEhrRedirectToast";
import { useProcessingSocket } from "../hooks/useProcessingSocket";

function NavItem({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `px-3 py-2 text-sm font-medium rounded-md transition ${
          isActive
            ? "bg-brand-700 text-white"
            : "text-brand-100 hover:bg-brand-700 hover:text-white"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export function Layout(): JSX.Element {
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.email);
  const role = useAuthStore((s) => s.role);
  const tenantId = useAuthStore((s) => s.tenantId);
  const clear = useAuthStore((s) => s.clear);

  // Subscribe to the per-tenant processing event stream whenever the
  // user is signed in. Stages surface as toasts so the user gets live
  // feedback even when they're not looking at the document list.
  useProcessingSocket();

  // Surface the result of an EHR connect round-trip (?ehr=connected|denied|…).
  useEhrRedirectToast();

  const onLogout = async (): Promise<void> => {
    try {
      await auth.logout();
    } catch {
      // Server-side logout is a no-op in MVP per Phase 1 — if it
      // fails we still want the local session gone.
    }
    clear();
    pushToast("info", "Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-brand-800 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-6">
            <NavLink to="/" className="flex items-center gap-2 text-lg font-semibold">
              <span aria-hidden className="inline-block h-6 w-6 rounded bg-brand-200" />
              <span>CaseLens</span>
            </NavLink>
            <nav className="flex items-center gap-1" aria-label="Primary">
              <NavItem to="/" label="Dashboard" />
              <NavItem to="/cases" label="Cases" />
              <NavItem to="/deadlines" label="Deadlines" />
              <NavItem to="/statutes" label="Statutes" />
              <NavItem to="/settings" label="Settings" />
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs leading-tight">
              <div className="font-medium" data-testid="nav-email">
                {email ?? "—"}
              </div>
              <div className="text-brand-200">
                tenant #{tenantId ?? "—"} · {role ?? "—"}
              </div>
            </div>
            <button type="button" onClick={onLogout} className="btn-secondary">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 bg-white py-3 text-center text-xs text-slate-500">
        CaseLens MVP · documents stay on this tenant · v0.1
      </footer>
      <ToastContainer />
    </div>
  );
}
